import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, TrendingUp, CreditCard, Smartphone, Globe } from "lucide-react";

/**
 * AdminSubscriptions
 *
 * MRR-by-source dashboard. Shows how the $49.99/mo Practitioner
 * subscription revenue splits across Stripe (web), Apple IAP (iOS),
 * and Google Play (Android). Computes net-after-fee for each channel
 * so the admin can compare "what we charge" vs "what we net".
 *
 * KPIs:
 *   • Active subscribers (total)
 *   • Gross MRR / Net MRR / Platform fees paid
 *   • Annual net run-rate
 *   • Per-channel breakdown with horizontal stacked bar
 *   • Lifetime Stripe invoice gross (context)
 */
export default function AdminSubscriptions() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([
        api.get("/admin/subscriptions/breakdown"),
        api.get("/admin/subscriptions/trend", { params: { days: 7 } }),
      ]);
      setData(b.data);
      setTrend(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load subscription breakdown");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = data?.totals;
  const bySource = data?.bySource || [];
  const grossMrr = totals?.grossMrr || 0;

  // Color tokens per channel — kept consistent so the admin can quickly
  // map color → channel without reading labels.
  const sourceColor = {
    stripe:      { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-300", bar: "bg-emerald-500", Icon: Globe },
    apple_iap:   { bg: "bg-foreground/10", border: "border-foreground/30", text: "text-foreground", bar: "bg-foreground/80", Icon: Smartphone },
    google_play: { bg: "bg-blue-500/15", border: "border-blue-500/40", text: "text-blue-300", bar: "bg-blue-500", Icon: Smartphone },
  };

  return (
    <div className="space-y-6" data-testid="admin-subscriptions">
      <div>
        <div className="label-mystic">Revenue Mix</div>
        <h2 className="font-serif text-3xl mt-1">Subscription Channels</h2>
        <p className="text-foreground/60 text-sm mt-2 max-w-2xl">
          MRR breakdown across the three billing channels. Net values
          subtract each platform&apos;s cut — Stripe 2.9% + $0.30, Apple/Google 15%
          (Small Business Program rate; bump to 30% if you exceed $1M/yr).
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-secondary/30 hover:bg-secondary/10 text-xs uppercase tracking-[0.22em] disabled:opacity-50"
          data-testid="admin-subs-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        {data && (
          <span className="text-xs text-foreground/50 ml-auto">
            Price: <span className="text-secondary">${data.priceUsd}/mo</span>
          </span>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Active subscribers" value={totals?.activeSubscribers ?? "—"} icon={<CreditCard size={11} />} testid="kpi-active" />
        <Kpi label="Gross MRR"          value={fmtUsd(totals?.grossMrr)} testid="kpi-gross" accent="emerald" />
        <Kpi label="Net MRR (after fees)" value={fmtUsd(totals?.netMrr)} testid="kpi-net" accent="emerald" />
        <Kpi label="Net ARR run-rate"   value={fmtUsd(totals?.annualNetRunRate)} icon={<TrendingUp size={11} />} testid="kpi-arr" accent="amber" />
      </div>

      {/* Stacked-bar share */}
      {grossMrr > 0 && (
        <div className="rounded-md border border-secondary/15 p-4" data-testid="admin-subs-stacked-bar">
          <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/60 mb-2">Channel mix (by gross MRR)</div>
          <div className="flex h-3 w-full rounded overflow-hidden">
            {bySource.map((r) => (
              <div
                key={r.source}
                className={sourceColor[r.source]?.bar || "bg-secondary"}
                style={{ width: `${(r.grossMrr / grossMrr) * 100}%` }}
                title={`${r.label}: ${fmtUsd(r.grossMrr)}`}
                data-testid={`stacked-${r.source}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-[11px]">
            {bySource.map((r) => (
              <span key={r.source} className="inline-flex items-center gap-1.5">
                <span className={`inline-block w-2.5 h-2.5 rounded-sm ${sourceColor[r.source]?.bar || "bg-secondary"}`} />
                <span className="text-foreground/70">{r.label}</span>
                <span className="text-foreground/50">·</span>
                <span className="text-secondary">{Math.round((r.grossMrr / grossMrr) * 100)}%</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Per-channel cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-subs-by-source">
        {bySource.map((r) => {
          const tone = sourceColor[r.source] || sourceColor.stripe;
          return (
            <div
              key={r.source}
              className={`p-5 rounded-md border ${tone.border} ${tone.bg}`}
              data-testid={`channel-${r.source}`}
            >
              <div className={`flex items-center gap-2 ${tone.text}`}>
                <tone.Icon size={14} strokeWidth={1.75} />
                <span className="text-[10px] uppercase tracking-[0.22em]">{r.label}</span>
              </div>
              <div className="font-serif text-3xl mt-2" data-testid={`subs-count-${r.source}`}>{r.subscribers}</div>
              <div className="text-foreground/60 text-xs mt-0.5">subscribers</div>
              {trend?.series?.[r.source] && (
                <Sparkline
                  values={trend.series[r.source]}
                  color={tone.bar}
                  days={trend.days}
                  newThisWeek={trend.totals?.[r.source] || 0}
                />
              )}
              <div className="mt-4 space-y-1 text-sm">
                <Row label="Gross MRR" value={fmtUsd(r.grossMrr)} testid={`gross-${r.source}`} />
                <Row label={`Platform fee (${r.feePct}%${r.feeFlat ? ` + $${r.feeFlat}` : ""})`} value={`-${fmtUsd(r.platformFeeUsd)}`} dim />
                <Row label="Net MRR" value={fmtUsd(r.netMrr)} highlight testid={`net-${r.source}`} />
              </div>
            </div>
          );
        })}
      </div>

      {data?.lifetime && (
        <div className="text-xs text-foreground/50 pt-2 border-t border-secondary/10">
          Lifetime context · Stripe invoices paid: <b className="text-foreground/70">{data.lifetime.stripeInvoicesPaid}</b> · gross lifetime: <b className="text-foreground/70">{fmtUsd(data.lifetime.stripeGrossLifetime)}</b>
        </div>
      )}
    </div>
  );
}

function fmtUsd(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Kpi({ label, value, icon, accent, testid }) {
  const tone =
    accent === "emerald" ? "border-emerald-500/30 bg-emerald-500/[0.05]"
    : accent === "amber" ? "border-amber-500/30 bg-amber-500/[0.05]"
    : "border-secondary/20 bg-secondary/[0.04]";
  return (
    <div className={`p-4 rounded-md border ${tone}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/60 flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="font-serif text-3xl mt-1">{value}</div>
    </div>
  );
}

function Row({ label, value, dim, highlight, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-2" data-testid={testid}>
      <span className={`text-xs ${dim ? "text-foreground/40" : "text-foreground/65"}`}>{label}</span>
      <span className={`font-mono ${highlight ? "text-secondary text-base" : dim ? "text-foreground/40" : "text-foreground/85"}`}>{value}</span>
    </div>
  );
}

/**
 * Sparkline — tiny inline SVG showing N daily values.
 * Renders as `bar` (not line) so a single new sub is still visible on
 * empty days, and "0" days clearly read as zero gaps.
 *
 * Props:
 *   values: number[] (length = days)
 *   color: tailwind bg-class for the bars (e.g. "bg-emerald-500")
 *   days: number of days shown (for the caption)
 *   newThisWeek: pre-computed total to show as caption
 */
function Sparkline({ values, color, days, newThisWeek }) {
  const max = Math.max(1, ...values);
  return (
    <div className="mt-2" data-testid="sparkline">
      <div className="flex items-end gap-0.5 h-7" aria-label={`${newThisWeek} new in last ${days} days`}>
        {values.map((v, i) => (
          <div
            key={i}
            className={`flex-1 rounded-sm ${color} ${v === 0 ? "opacity-25" : "opacity-90"}`}
            style={{ height: `${Math.max(8, (v / max) * 100)}%` }}
            title={`Day ${i + 1}: ${v} new`}
          />
        ))}
      </div>
      <div className="text-[10px] text-foreground/55 mt-1 uppercase tracking-[0.18em]">
        {newThisWeek} new · last {days} days
      </div>
    </div>
  );
}
